import { Box, Center, Flex, Icon, Text } from '@chakra-ui/react';
import { FaCheck } from '@react-icons/all-files/fa/FaCheck';
import { useField } from 'formik';
import { useState } from 'react';

import { MultiValue, SchemaQuestion } from '../encodeDecodeAnswers';

type MultiSelectToggleCardProps = {
  question: SchemaQuestion;
};

const EMPTY_MULTI: MultiValue = { issues: [], noIssues: false };

const MultiSelectToggleCard = ({ question }: MultiSelectToggleCardProps) => {
  const [field, , helpers] = useField(question.questionId);
  const current: MultiValue = (field.value as MultiValue) ?? EMPTY_MULTI;
  const options = question.options ?? [];
  const issueOptions = options.filter((o) => !o.exclusive);

  const [expanded, setExpanded] = useState(current.issues.length > 0);

  const isClear = current.noIssues;
  const isFlagged = expanded && !isClear;

  const borderColor = isClear ? 'oceanBoatBlue' : isFlagged ? 'bananaMania' : 'gray.200';
  const headerBg = isClear ? 'iceBlue' : isFlagged ? 'amberLight' : 'white';
  const titleColor = isClear ? 'oceanBoatBlue' : isFlagged ? 'darkGoldenrod' : 'leaseEndBlue';

  const selectNoIssues = () => {
    setExpanded(false);
    helpers.setValue({ issues: [], noIssues: true });
  };
  const selectHasIssues = () => {
    if (isFlagged) return;
    setExpanded(true);
    if (isClear) {
      helpers.setValue({ issues: [], noIssues: false });
    }
  };
  const toggleIssue = (value: string) => {
    const next = current.issues.includes(value)
      ? current.issues.filter((v) => v !== value)
      : [...current.issues, value];
    helpers.setValue({ issues: next, noIssues: false });
  };

  return (
    <Box
      border="1.5px solid"
      borderColor={borderColor}
      borderRadius="14px"
      overflow="hidden"
      mb={3}
    >
      <Flex
        align="center"
        justify="flex-end"
        flexWrap="wrap"
        p="14px 16px"
        bg={headerBg}
        gap={3}
        transition="all 0.15s ease"
      >
        <Text fontSize="md" fontWeight="700" color={titleColor} flex="1 0 auto" maxW="100%">
          {question.label}
        </Text>
        <Flex gap="4px" bg="grayLightBackground" borderRadius="full" p="3px" flexShrink={0}>
          <Box
            as="button"
            type="button"
            onClick={selectNoIssues}
            px="12px"
            py="6px"
            borderRadius="full"
            fontSize="xs"
            fontWeight="600"
            bg={isClear ? 'oceanBoatBlue' : 'transparent'}
            color={isClear ? 'white' : 'grayLight'}
            cursor="pointer"
          >
            No issues
          </Box>
          <Box
            as="button"
            type="button"
            onClick={selectHasIssues}
            px="12px"
            py="6px"
            borderRadius="full"
            fontSize="xs"
            fontWeight="600"
            bg={isFlagged ? 'darkGoldenrod' : 'transparent'}
            color={isFlagged ? 'white' : 'grayLight'}
            cursor="pointer"
          >
            Has issues
          </Box>
        </Flex>
      </Flex>
      {isFlagged && (
        <Box px={4} pb={4} pt={3} bg="amberLight" borderTop="1px solid" borderColor="bananaMania">
          <Text
            fontSize="xs"
            fontWeight="700"
            color="darkGoldenrod"
            textTransform="uppercase"
            letterSpacing="0.04em"
            mb={2}
          >
            Select all that apply
          </Text>
          {issueOptions.map((o) => {
            const checked = current.issues.includes(o.value);
            return (
              <Flex
                as="button"
                type="button"
                key={o.value}
                onClick={() => toggleIssue(o.value)}
                align="center"
                gap={3}
                py="8px"
                w="100%"
                textAlign="left"
                cursor="pointer"
              >
                <Center
                  w="20px"
                  h="20px"
                  borderRadius="5px"
                  border="2px solid"
                  borderColor={checked ? 'darkGoldenrod' : 'gainsboro'}
                  bg={checked ? 'darkGoldenrod' : 'white'}
                  flexShrink={0}
                >
                  {checked && <Icon as={FaCheck} color="white" boxSize="10px" />}
                </Center>
                <Text fontSize="sm" color="grayDark">
                  {o.label}
                </Text>
              </Flex>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default MultiSelectToggleCard;
